import React, { useEffect, useState } from 'react'
import rawDataService, { ProjectNode, UploadBatchItem } from '../services/rawDataService'
import systemService, { TriggerLabelSchemas } from '../services/systemService'
import { formatLocaleDatetime } from '../utils/date'

interface RawPathPickerModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (list: { rawNodeId: number; rawBatchId: number | null; pathLabel: string }[]) => void
  uploadMode?: 'manual' | 'auto'
  initialSelections?: { rawNodeId: number; rawBatchId: number | null; pathLabel: string }[]
  persistState?: {
    mode?: 'manual' | 'auto'
    rootNodes?: ProjectNode[]
    nodes?: ProjectNode[]
    expandedIds?: number[]
    selectedLeafId?: number | null
    history?: UploadBatchItem[]
    selectedItems?: { rawNodeId: number; rawBatchId: number | null; pathLabel: string }[]
  }
  onStateChange?: (state: {
    mode: 'manual' | 'auto'
    rootNodes: ProjectNode[]
    nodes: ProjectNode[]
    expandedIds: number[]
    selectedLeafId: number | null
    history: UploadBatchItem[]
    selectedItems: { rawNodeId: number; rawBatchId: number | null; pathLabel: string }[]
  }) => void
}

export default function RawPathPickerModal({
  isOpen,
  onClose,
  onSelect,
  uploadMode = 'manual',
  initialSelections,
  persistState,
  onStateChange,
}: RawPathPickerModalProps) {
  const [rootNodes, setRootNodes] = useState<ProjectNode[]>([])
  const [nodes, setNodes] = useState<ProjectNode[]>([])
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [selectedLeaf, setSelectedLeaf] = useState<ProjectNode | null>(null)
  const [history, setHistory] = useState<UploadBatchItem[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedItems, setSelectedItems] = useState<{ rawNodeId: number; rawBatchId: number | null; pathLabel: string }[]>([])
  const [mode, setMode] = useState<'manual' | 'auto'>(uploadMode)
  const [triggerLabels, setTriggerLabels] = useState<TriggerLabelSchemas | null>(null)

  const sortNodes = (list: ProjectNode[]) => {
    // 표시 라벨 기준 정렬: 자동 업로드(라벨 매핑)에서도 사용자가 보는 순서가 자연스럽게 맞춰짐
    return [...(list || [])].sort((a, b) => {
      const la = (resolveNodeLabel(a) || a.name || '').toString()
      const lb = (resolveNodeLabel(b) || b.name || '').toString()
      return la.localeCompare(lb, undefined, { numeric: true, sensitivity: 'base' })
    })
  }

  useEffect(() => {
    if (!isOpen) return
    // 모달이 열릴 때만 초기 모드/선택 상태를 세팅하고,
    // 이후 persistState 변경에는 반응하지 않도록 한다(무한 리셋 방지).
    const initialMode = persistState?.mode || uploadMode
    setMode(initialMode)
    setSelectedItems(initialSelections || persistState?.selectedItems || [])
  }, [isOpen, uploadMode, initialSelections])

  useEffect(() => {
    if (!isOpen) return
    ;(async () => {
      setLoading(true)
      try {
        if (persistState && persistState.mode === mode && (persistState.nodes?.length || 0) > 0) {
          setRootNodes(sortNodes(persistState.rootNodes || []))
          // nodes 는 평면 구조이므로, 렌더 시점에 parent 별로 다시 정렬한다
          setNodes(persistState.nodes || [])
          setExpanded(new Set(persistState.expandedIds || []))
          const sel = (persistState.selectedLeafId != null) ? (persistState.nodes || []).find((n: ProjectNode) => n.id === persistState.selectedLeafId) || null : null
          setSelectedLeaf(sel)
          setHistory(persistState.history || [])
          if (initialSelections && initialSelections.length > 0) {
            setSelectedItems(initialSelections)
          } else if (persistState?.selectedItems && persistState.selectedItems.length > 0) {
            setSelectedItems(persistState.selectedItems)
          }
        } else {
          const roots = await rawDataService.getRootNodes(mode)
          const sortedRoots = sortNodes(roots)
          setRootNodes(sortedRoots)
          setNodes(sortedRoots)
          setExpanded(new Set())
          setSelectedLeaf(null)
          setHistory([])
          if (initialSelections && initialSelections.length > 0) {
            setSelectedItems(initialSelections)
          } else if (persistState?.selectedItems && persistState.selectedItems.length > 0) {
            setSelectedItems(persistState.selectedItems)
          }
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [mode, isOpen])

  // 자동 업로드 모드에서만 트리거 라벨 스키마 로딩 (원천데이터관리와 동일한 기준)
  useEffect(() => {
    if (!isOpen || mode !== 'auto') {
      setTriggerLabels(null)
      return
    }
    let mounted = true
    ;(async () => {
      try {
        const labels = await systemService.getTriggerLabels()
        if (mounted) {
          setTriggerLabels(labels)
        }
      } catch {
        if (mounted) setTriggerLabels(null)
      }
    })()
    return () => {
      mounted = false
    }
  }, [isOpen, mode])

  const resolveNodeLabel = (node: ProjectNode): string => {
    // 자동 업로드 트리에서만 라벨 매핑을 적용
    if (mode !== 'auto' || !triggerLabels) return node.name

    // 루트(프로젝트) 또는 그 바로 아래까지만 라벨 치환
    const isRootLike = !node.parent_id || node.depth <= 1
    if (!isRootLike) return node.name

    const raw = node.name || ''
    if (!raw) return node.name

    // 예: "TRG_CON" → prefix="TRG", code="CON"
    //     "TRG-R_OBS" → prefix="TRG-R", code="OBS"
    const parts = raw.split('_').filter(Boolean)
    const prefix = parts.length >= 2 ? parts[0] : ''
    const code = parts.length >= 1 ? parts[parts.length - 1] : raw

    const schema = triggerLabels[prefix]
    const category = schema?.categories?.[code]
    const label = category?.display_name || category?.label

    if (label && prefix) {
      return `${prefix}_${label}`
    }
    if (label) {
      return label
    }
    // 매핑이 없으면 원래 이름 그대로
    return node.name
  }

  // 부모에 상태 동기화 (선택/트리/모드 변화 모두 포함)
  useEffect(() => {
    if (!onStateChange || !isOpen) return
    onStateChange({
      mode,
      rootNodes,
      nodes,
      expandedIds: Array.from(expanded),
      selectedLeafId: selectedLeaf?.id ?? null,
      history,
      selectedItems,
    })
  }, [isOpen, onStateChange, mode, rootNodes, nodes, expanded, selectedLeaf, history, selectedItems])

  const toggleNode = async (node: ProjectNode) => {
    const newSet = new Set(expanded)
    if (newSet.has(node.id)) newSet.delete(node.id)
    else {
      newSet.add(node.id)
      // load children
      const children = await rawDataService.getNodeChildren(node.id, mode)
      const sortedChildren = sortNodes(children)
      let updatedNodes: ProjectNode[] = []
      setNodes(prev => {
        const ids = new Set(prev.map(n => n.id))
        const merged = [...prev]
        sortedChildren.forEach(c => { if (!ids.has(c.id)) merged.push(c) })
        updatedNodes = merged
        return merged
      })
    }
    setExpanded(newSet)
    if (onStateChange)
      onStateChange({
        mode,
        rootNodes,
        nodes,
        expandedIds: Array.from(newSet),
        selectedLeafId: selectedLeaf?.id ?? null,
        history,
        selectedItems,
      })
  }

  const selectLeaf = async (node: ProjectNode) => {
    setSelectedLeaf(node)
    try {
      const list = await rawDataService.listUploadHistory(node.id, 0, 50, mode)
      setHistory(list)
      if (onStateChange)
        onStateChange({
          mode,
          rootNodes,
          nodes,
          expandedIds: Array.from(expanded),
          selectedLeafId: node.id,
          history: list,
          selectedItems,
        })
    } catch {
      setHistory([])
      if (onStateChange)
        onStateChange({
          mode,
          rootNodes,
          nodes,
          expandedIds: Array.from(expanded),
          selectedLeafId: node.id,
          history: [],
          selectedItems,
        })
    }
  }

  const renderNode = (node: ProjectNode, level = 0) => {
    const isExpanded = expanded.has(node.id)
    const children = sortNodes(nodes.filter(n => n.parent_id === node.id))
    const hasChildren = children.length > 0
    const hasChildrenHint = hasChildren || ((node as any).child_count || 0) > 0
    return (
      <div key={node.id}>
        <div
          className={`flex items-center py-1 px-2 cursor-pointer ${selectedLeaf?.id === node.id ? 'bg-gray-100' : ''}`}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onClick={async () => {
            if (!hasChildren) {
              try {
                const fetched = await rawDataService.getNodeChildren(node.id, mode)
                if (fetched && fetched.length > 0) {
                setNodes(prev => {
                  const ids = new Set(prev.map(n => n.id))
                  const merged = [...prev]
                  sortNodes(fetched).forEach(c => { if (!ids.has(c.id)) merged.push(c) })
                  return merged
                })
                  setExpanded(prev => { const ns = new Set(prev); ns.add(node.id); return ns })
                  return
                }
              } catch {}
              selectLeaf(node)
              return
            }
            await toggleNode(node)
          }}
        >
          <svg 
            className="w-5 h-5 text-gray-700 mr-2" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          {hasChildrenHint && (
            <button
              onClick={async (e) => { e.stopPropagation(); await toggleNode(node) }}
              className="mr-1 w-4 h-4 flex items-center justify-center"
              aria-label={isExpanded ? '접기' : '펼치기'}
              title={isExpanded ? '접기' : '펼치기'}
            >
              <svg 
                className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          <span className="text-sm text-gray-800">{resolveNodeLabel(node)}</span>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {children.map(c => renderNode(c, level + 1))}
          </div>
        )}
      </div>
    )
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">원천 경로 선택 (히스토리까지)</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="mb-3">
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-1">
            {[
              { key: 'manual' as const, label: '수동 업로드' },
              { key: 'auto' as const, label: '자동 업로드' }
            ].map(tab => {
              const active = mode === tab.key
              return (
                <button
                  key={tab.key}
                  className={`px-4 py-2 text-sm rounded-md transition-colors ${active ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-900'}`}
                  onClick={() => setMode(tab.key)}
                  type="button"
                >
                  {tab.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4" style={{ minHeight: 480 }}>
          <div className="border border-gray-200 rounded overflow-y-auto">
            {loading ? (
              <div className="p-4 text-gray-500">로딩 중...</div>
            ) : rootNodes.length === 0 ? (
              <div className="p-4 text-gray-500">원천 데이터가 없습니다.</div>
            ) : (
              sortNodes(rootNodes).map(r => renderNode(r))
            )}
          </div>
          <div className="border border-gray-200 rounded overflow-y-auto p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm text-gray-700">선택된 스트림: {selectedLeaf ? resolveNodeLabel(selectedLeaf) : '-'}</div>
              <button
                className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-50"
                onClick={async () => {
                  if (!selectedLeaf) return
                  setLoading(true)
                  try {
                    const list = await rawDataService.listUploadHistory(selectedLeaf.id, 0, 50, mode)
                    setHistory(list)
                    if (onStateChange) onStateChange({ mode, rootNodes, nodes, expandedIds: Array.from(expanded), selectedLeafId: selectedLeaf.id, history: list, selectedItems })
                  } finally {
                    setLoading(false)
                  }
                }}
                disabled={!selectedLeaf || loading}
              >새로고침</button>
            </div>
            <div className="mb-1 text-xs text-gray-600">선택된 항목: {selectedItems.length}개</div>
            <div className="mb-3 flex flex-wrap gap-2">
              {selectedItems.length === 0 ? (
                <span className="text-xs text-gray-500">선택된 항목이 없습니다.</span>
              ) : (
                selectedItems.map((s, idx) => (
                  <span key={`${s.rawNodeId}-${s.rawBatchId}-${idx}`} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 text-gray-800 text-xs">
                    {s.pathLabel}
                    <button
                      type="button"
                      className="text-gray-500 hover:text-gray-700"
                      onClick={() => setSelectedItems(prev => prev.filter((_, i) => i !== idx))}
                      aria-label="선택 제거"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </span>
                ))
              )}
            </div>
            <div className="space-y-2">
              {selectedLeaf == null ? (
                <div className="text-gray-500">좌측에서 스트림(리프)을 선택하세요.</div>
              ) : history.length === 0 ? (
                <div className="text-gray-500">히스토리가 없습니다.</div>
              ) : (
                history.map(h => {
                  const exists = selectedItems.findIndex(s => s.rawNodeId === (selectedLeaf?.id || -1) && s.rawBatchId === h.id) >= 0
                  const statusNorm = String(h.status || '').toUpperCase()
                  // 완료 판정: 서버 상태가 COMPLETED 이거나 완료시각이 존재할 때만
                  const isCompleted = statusNorm === 'COMPLETED' || !!h.completed_at
                  const statusLabel = statusNorm || (isCompleted ? 'COMPLETED' : 'PROCESSING')
                  return (
                    <label
                      key={h.id}
                      className={`p-3 border border-gray-200 rounded flex items-center justify-between ${isCompleted ? 'cursor-pointer hover:bg-gray-50' : 'opacity-50 cursor-not-allowed'}`}
                      title={isCompleted ? '' : '작업이 완료되지 않아 선택할 수 없습니다.'}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={exists}
                          disabled={!isCompleted}
                          onChange={(e) => {
                              if (!selectedLeaf || !isCompleted) return
                              if (e.target.checked) {
                              setSelectedItems(prev => [...prev, { rawNodeId: selectedLeaf.id, rawBatchId: h.id, pathLabel: `${resolveNodeLabel(selectedLeaf)} · ${h.source_filename || `배치 ${h.id}`}` }])
                            } else {
                              setSelectedItems(prev => prev.filter(s => !(s.rawNodeId === selectedLeaf.id && s.rawBatchId === h.id)))
                            }
                          }}
                        />
                        <div className="text-sm text-gray-800 truncate">
                          <div className="font-medium flex items-center gap-2">
                            <span>{h.source_filename || `배치 ${h.id}`}</span>
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] ${isCompleted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {statusLabel}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">항목 {h.total_items} · 시작 {formatLocaleDatetime(h.started_at)}</div>
                        </div>
                      </div>
                    </label>
                  )
                })
              )}
              {/* 스트림 전체 선택 제거: 히스토리 필수 선택 */}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <button
            className="px-3 py-1 text-sm border border-gray-300 rounded"
            onClick={() => setSelectedItems([])}
          >모두 해제</button>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 text-sm border border-gray-300 rounded"
              onClick={onClose}
            >취소</button>
            <button
              className="px-3 py-1 text-sm bg-gray-800 text-white rounded disabled:opacity-50"
              disabled={selectedItems.length === 0}
              onClick={() => onSelect(selectedItems)}
            >선택 완료</button>
          </div>
        </div>

      </div>
    </div>
  )
}


